import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FlaskConical, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';

import {
  licensingApi,
  type LicensingE2EEntitlementAction,
} from '@/api/licensing';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';

const labels: Record<LicensingE2EEntitlementAction, string> = {
  canonical: 'По расчётам',
  ok: 'Разрешено',
  suspended: 'Приостановлено',
};

export function LicensingE2EEntitlementPanel({
  clientId,
}: {
  clientId: number;
}) {
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');
  const [pendingAction, setPendingAction] =
    useState<LicensingE2EEntitlementAction | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const queryKey = ['licensing', 'e2e-entitlement', clientId] as const;
  const state = useQuery({
    queryKey,
    queryFn: () => licensingApi.getClientE2EEntitlement(clientId),
    retry: false,
  });
  const mutation = useMutation({
    mutationFn: (action: LicensingE2EEntitlementAction) =>
      licensingApi.setClientE2EEntitlement(clientId, action, reason.trim()),
    onSuccess: (next) => {
      queryClient.setQueryData(queryKey, next);
      setPendingAction(null);
      setReason('');
      toast.success('Тестовый статус обновлён');
    },
    onError: () => {
      toast.error('Не удалось изменить тестовый статус');
    },
  });

  if (state.isLoading) {
    return (
      <section
        aria-live="polite"
        className="rounded-lg border border-dashed bg-muted/30 p-4 text-sm text-muted-foreground"
      >
        Проверяю доступность тестового статуса…
      </section>
    );
  }
  if (state.isError || !state.data) {
    return (
      <section
        role="alert"
        className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm"
      >
        <p className="font-medium">Тестовый статус недоступен</p>
        <p className="mt-1 text-muted-foreground">
          Проверьте роль, делегирование клиента и dev-конфигурацию API.
        </p>
      </section>
    );
  }

  const value = state.data;
  const selectedAction: LicensingE2EEntitlementAction =
    value.source === 'dev_e2e' &&
    (value.effectiveStatus === 'ok' ||
      value.effectiveStatus === 'suspended')
      ? value.effectiveStatus
      : 'canonical';
  const canConfirm = reason.trim().length > 0 && reason.trim().length <= 500;

  return (
    <section className="rounded-lg border border-amber-300/60 bg-amber-50/50 p-4 dark:bg-amber-950/10">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-500/15">
          <FlaskConical className="h-5 w-5 text-amber-700 dark:text-amber-300" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="font-medium">Тест лицензирования</h2>
            <Badge variant="outline">только dev</Badge>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            Управляет только проверкой отключения WGAG. Счета и оплаты не меняются.
          </p>
        </div>
      </div>

      <dl className="mt-4 grid gap-2 text-sm">
        <StatusRow label="По расчётам" value={value.canonicalStatus} />
        <StatusRow label="Сейчас действует" value={value.effectiveStatus} />
        <StatusRow
          label="Источник"
          value={value.source === 'dev_e2e' ? 'dev E2E' : 'расчёты'}
        />
        <StatusRow
          label="Проверено"
          value={new Date(value.evaluatedAt).toLocaleString('ru-RU')}
        />
      </dl>

      {value.controlHealth === 'malformed' && (
        <div role="alert" className="mt-3 flex gap-2 rounded-md border border-destructive/30 p-3 text-sm">
          <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
          Некорректная тестовая запись игнорируется. Верните режим «По расчётам»
          или установите новый статус.
        </div>
      )}

      <label className="mt-4 grid gap-1 text-sm">
        <span className="font-medium">Причина изменения</span>
        <Textarea
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          maxLength={500}
          placeholder="Например: WGAG T135 suspend/restore"
          disabled={mutation.isPending}
          className="min-h-20"
        />
      </label>

      <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
        {(['canonical', 'ok', 'suspended'] as const).map((action) => (
          <Button
            key={action}
            type="button"
            variant={action === 'suspended' ? 'destructive' : 'outline'}
            disabled={
              mutation.isPending ||
              action === selectedAction ||
              !canConfirm
            }
            onClick={(event) => {
              triggerRef.current = event.currentTarget;
              setPendingAction(action);
            }}
          >
            {labels[action]}
          </Button>
        ))}
      </div>

      <AlertDialog
        open={pendingAction !== null}
        onOpenChange={(open) => !open && setPendingAction(null)}
      >
        <AlertDialogContent
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            triggerRef.current?.focus();
          }}
        >
          <AlertDialogHeader>
            <AlertDialogTitle>Изменить тестовый статус?</AlertDialogTitle>
            <AlertDialogDescription>
              Будет выбран режим «{pendingAction ? labels[pendingAction] : ''}».
              Следующий refresh licensing-platform увидит это решение.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={mutation.isPending}>
              Отмена
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={!pendingAction || !canConfirm || mutation.isPending}
              onClick={(event) => {
                event.preventDefault();
                if (pendingAction) mutation.mutate(pendingAction);
              }}
            >
              Подтвердить
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}

function StatusRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-mono text-xs">{value}</dd>
    </div>
  );
}
